/**
 * FT-122: Compliance Item Modal
 *
 * Add/Edit modal for compliance register items.
 * Fields: Name, Category, Expiry/Review Date, Responsible Person, Document Link, Notes
 */

import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { COMPLIANCE_CATEGORIES } from '../../../../hooks/domain/useComplianceRegister';

const ComplianceItemModal = ({ item, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    category: 'certificate',  // FT-122c: renamed from vessel_certificate
    expiryDate: '',
    responsiblePerson: '',
    documentLink: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Populate form when editing
  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name || '',
        category: item.category || 'certificate',  // FT-122c: renamed from vessel_certificate
        expiryDate: item.expiryDate || '',
        responsiblePerson: item.responsiblePerson || '',
        documentLink: item.documentLink || '',
        notes: item.notes || ''
      });
    }
  }, [item]);

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Document name is required';
    }
    if (formData.documentLink && !formData.documentLink.startsWith('http')) {
      newErrors.documentLink = 'Link must start with http:// or https://';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      await onSave({
        ...formData,
        name: formData.name.trim(),
        responsiblePerson: formData.responsiblePerson.trim(),
        documentLink: formData.documentLink.trim() || null,
        notes: formData.notes.trim(),
        expiryDate: formData.expiryDate || null
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when field is modified
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />

      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {item ? 'Edit Compliance Item' : 'Add Compliance Item'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Document Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g. Loadline Certificate"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={formData.category}
                onChange={(e) => handleChange('category', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              >
                {Object.entries(COMPLIANCE_CATEGORIES).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            {/* Expiry / Review Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expiry / Review Date
              </label>
              <input
                type="date"
                value={formData.expiryDate}
                onChange={(e) => handleChange('expiryDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Leave blank if no expiry date applies
              </p>
            </div>

            {/* Responsible Person */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Responsible Person
              </label>
              <input
                type="text"
                value={formData.responsiblePerson}
                onChange={(e) => handleChange('responsiblePerson', e.target.value)}
                placeholder="e.g. John Smith"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Document Link */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Document Link
              </label>
              <input
                type="url"
                value={formData.documentLink}
                onChange={(e) => handleChange('documentLink', e.target.value)}
                placeholder="https://drive.google.com/..."
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                  errors.documentLink ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.documentLink && <p className="mt-1 text-sm text-red-500">{errors.documentLink}</p>}
              <p className="mt-1 text-xs text-gray-500">
                Link to Google Drive, SharePoint, or other document storage
              </p>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Additional notes..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50"
                disabled={saving}
              >
                {saving ? 'Saving...' : item ? 'Update' : 'Add Item'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ComplianceItemModal;
